/**
 * Résolution d'un chemin d'annuaire — l'I/O, séparée de la décision
 * (`./resolvePath.js`). Le `fetch` est injecté : côté SSR c'est `node-fetch`,
 * côté navigateur c'est `src/utils/api`. Aucun réseau n'est câblé en dur ici,
 * ce qui rend le module testable sans serveur ni mock global.
 *
 * Contrat (FIX-542, contract.md — l'api est propriétaire) :
 *
 *   GET /annuaire/resolve?path=<chemin absolu>     public, non authentifié
 *
 * L'endpoint répond TOUJOURS 200 : « ce chemin n'a pas de Redirection » est un
 * fait, pas une erreur. Un 404 HTTP ici signifierait une erreur de routage ou un
 * endpoint absent — d'où le rejet plutôt que le silence.
 *
 *   200 { "found": false }
 *   200 { "found": true, "redirect": { "destination": "/annuaire-velo/aude/villepinte",
 *                                      "http_code": 301, "reason": "DEMANDE_ATELIER" } }
 *   200 { "found": true, "redirect": { "destination": null, "http_code": 410,
 *                                      "reason": "FICHE_ERRONEE" } }
 *
 * `http_code` est une donnée stockée, jamais une décision prise à la requête :
 * l'api sert les faits, le front sert le code (décision 9).
 */

export const ANNUAIRE_RESOLVE_ROUTE = '/annuaire/resolve';

/** Seuls codes admis par le contrat. Toute autre valeur est une violation. */
export const SUPPORTED_HTTP_CODES = [301, 410];

export const annuaireResolveRoute = (path) =>
  `${ANNUAIRE_RESOLVE_ROUTE}?path=${encodeURIComponent(path)}`;

/**
 * Réponse brute de l'api → `null` (aucune Redirection) ou
 * `{ httpCode, destination, reason }`.
 *
 * JETTE sur toute réponse hors contrat. L'appelant doit alors traiter le lookup
 * comme indisponible — jamais comme « ce chemin n'a jamais existé ».
 */
export function normalizeResolveResponse(json) {
  if (!json || typeof json !== 'object') {
    throw new Error('[annuaire] /annuaire/resolve: réponse illisible');
  }
  if (!json.found) return null;

  const { redirect } = json;
  if (!redirect || typeof redirect !== 'object') {
    throw new Error(
      '[annuaire] /annuaire/resolve: found=true sans objet redirect',
    );
  }

  const httpCode = Number(redirect.http_code);
  if (!SUPPORTED_HTTP_CODES.includes(httpCode)) {
    throw new Error(
      `[annuaire] /annuaire/resolve: http_code hors contrat (${redirect.http_code})`,
    );
  }

  return {
    httpCode,
    // Le contrat garantit `destination === null` si et seulement si le code est
    // 410 ; on le rend vrai côté front plutôt que de faire confiance.
    destination: httpCode === 410 ? null : redirect.destination || null,
    reason: redirect.reason || null,
  };
}

/**
 * Interroge `GET /annuaire/resolve` avec un `fetch` injecté.
 *
 * La distinction qui compte : un refus MOTIVÉ de l'api (4xx — typiquement
 * `422 INVALID_PATH` sur un chemin non absolu ou trop long) est un FAIT, donc
 * « aucune Redirection » ; une PANNE (5xx, transport, JSON illisible) est une
 * absence d'information, donc un rejet. Les confondre coûterait cher dans les
 * deux sens : traiter un 5xx comme « aucune Redirection » produirait des 404 de
 * masse pendant un incident, et traiter un 422 comme une panne produirait des
 * 503 sur des chemins simplement mal formés.
 *
 * Le 404 reste un rejet : le contrat impose un 200 sur tout chemin connu ou
 * inconnu, donc un 404 signifie que la route elle-même est absente (api pas
 * encore déployée) — une panne, pas un fait.
 *
 * @returns {Promise<?{httpCode:number, destination:?string, reason:?string}>}
 * @throws  sur panne de transport, 404, 5xx, JSON illisible ou hors contrat —
 *          l'appelant sert alors le filet ou un 503, jamais un 404.
 */
export async function lookupRedirect({
  apiBase,
  path,
  fetchFn,
  timeout = 5000,
}) {
  const response = await fetchFn(`${apiBase}${annuaireResolveRoute(path)}`, {
    headers: { Accept: 'application/json' },
    timeout,
  });

  // Seul `422 INVALID_PATH` est un refus MOTIVÉ, et c'est le seul 4xx que le
  // contrat nomme pour cet endpoint. On ne généralise surtout pas à « tout
  // 4xx » : un 429 (rate limit), un 403 (WAF) ou un 400 (proxy) sont des
  // incidents d'infrastructure — les traiter comme « aucune Redirection »
  // produirait des 404 de masse une fois le filet coupé.
  if (response && response.status === 422) {
    const body = await response.json().catch(() => null);
    if (body && body.code === 'INVALID_PATH') return null;
    throw new Error(
      '[annuaire] /annuaire/resolve a répondu 422 sans code INVALID_PATH',
    );
  }

  if (!response || !response.ok) {
    throw new Error(
      `[annuaire] /annuaire/resolve a répondu ${
        response ? response.status : 'rien'
      }`,
    );
  }

  return normalizeResolveResponse(await response.json());
}
